(ns frontend.ui.component.table.view
  (:require
    [frontend.ui.component.lp-portal.modal.edit-modals.edit-table-modal :as edit-table-modal]
    [frontend.ui.component.table.style :as style]
    [herb.core :refer [<class]]
    [reagent.core :as r]
    [frontend.ui.component.chart.view :as chart]
    [frontend.ui.component.chart.style :as chart-style]
    [ui.components.radix-ui.dropdown-menu.view :as dropdown-menu]
    [frontend.tenant-customizations :as tenant-customizations]
    [frontend.ui.style.theme.core :as theme]))

;; FIXME For some of these functions (`tr`, `td`) we would like to pass an
;; optional props map as first parameter and then a `child` and optional
;; `children` but this is not possible as we couldn't distinguish between cases
;; where the first and/or the latter are present. The current solution used is
;; to make the first (the props map) compulsory.
;; Other solutions:
;; - have two functions: `td` and `td-custom`
;; - use last position keyword arguments:
;;   `(tr […] :props {:span 2})` (see:
;;   https://clojure.org/guides/destructuring#_keyword_arguments)
;; - make all components take only one argument: the props map inside which we
;;   pass potential `children`

(defn td-props
  [{:keys [colspan] :as props}]
  (if-not colspan
    {:class (<class style/td props)}
    {:class   (<class style/td props)
     :colSpan colspan}))

(defn td
  "An HTML `table` `td` tag. Watch-out: takes a compulsory props parameter as first argument."
  [{:keys [colspan] :as props} child]
  [:td
   (merge
    {:class (<class style/td props)}
    (when colspan
      {:colSpan colspan}))
   child])

(defn th-props
  [props]
  {:class (<class style/th props)})

(defn th
  "An HTML `table` `th` tag. Watch-out: takes a compulsory props parameter as first argument."
  [props child]
  [:th
   {:class (<class style/th props)}
   child])

(defn tr-props
  [props]
  {:class (<class style/tr props)})

(defn tr
  "An HTML `table` `tr` tag. Watch-out: takes a compulsory props parameter as first argument."
  [{:keys [dom-key] :as props} & children]
  (into [:tr
         {:class (<class style/tr props)
          :key   dom-key}]
        (->> children
             (map-indexed (fn [idx child]
                            ^{:key (str dom-key "-" idx)}
                            child))
             (doall))))

(defn tfoot-tr
  "An HTML `table` `tr` tag. Watch-out: takes a compulsory props parameter as first argument."
  [{:keys [dom-key] :as props} & children]
  (into [:tr
         {:class (<class style/tfoot-tr props)
          :key   dom-key}]
        (->> children
             (map-indexed (fn [idx child]
                            ^{:key (str dom-key "-" idx)}
                            child))
             (doall))))

(defn tbody-props
  [props]
  {:class (<class style/tbody props)})

(defn tbody
  [{:keys [dom-key]} & children]
  (into [:tbody
         {:key dom-key}]
        (->> children
             (map-indexed (fn [idx child]
                            ^{:key (str dom-key "-" idx)}
                            child))
             (doall))))

(defn tfoot
  [{:keys [dom-key]} & children]
  (into [:tfoot
         {:key dom-key}]
        (->> children
             (map-indexed (fn [idx child]
                            ^{:key (str dom-key "-" idx)}
                            child))
             (doall))))

(defn thead
  [& table-head]
  (into [:thead]
        table-head))

(defn container-props
  []
  {:class (<class style/table-wrapper)})

(defn table-props
  [props]
  {:class (<class style/table props)})

(defn table
  "Create a bare `[:table …]` where `children` must be proper `[thead …]` and `[tbody …]` manually
  created by you (see `table*`)."
  [{:keys [id overflow?]} & children]
  (let [reagent-table (into [:table
                             {:class (<class style/table nil)}]
                            children)]
    (if-not overflow?
      reagent-table
      [:div
       {:class (<class style/table-wrapper)
        :id    id}
       reagent-table])))

(defn table-from-values
  "Create a full `[:table …]` along with proper `[:thead …]`, `[:tbody …]`, `[:tr …]` etc from
  values passed as the first argument. The second argument is an optional vector of UI
  transformation to apply to styling of rows (see `table`)."
  ;; Please be sure you read and understood the following before modifying this function:
  ;; https://stackoverflow.com/questions/37164091/how-do-i-loop-through-a-subscribed-collection-in-re-frame-and-display-the-data-a/37186230#37186230
  ;; Values passed to this component are considered "quite static" (in the meaning of the post):
  ;; they are values extracted once in a while from LP Portal spreadsheets. To circumvent the
  ;; usual React message "Each child in an array should have a unique key prop", we use the
  ;; "into/map/vector" scheme described in the post.
  [footer-row? [label-row & value-rows] rows-props]
  (let [row-styles (->> value-rows
                        (map-indexed (fn [idx _v]
                                       (nth rows-props idx nil)))
                        (doall))]
    [:div
     {:class (<class style/scroll-container)}
     [table {}
      [thead {}
       (into [tr {}]
             (map (fn [label]
                    (vector th {} label))
                  label-row))]
      (into [tbody {}]
            (map (fn [row row-props]
                   ;; Row props are applied to row's tds and not the tr
                   (into [tr {}]
                         (map-indexed (fn [cell-idx value]
                                        (vector td
                                                (merge row-props
                                                       (nth (:cell-props row-props) cell-idx nil))
                                                value))
                                      row)))
                 (if footer-row? (butlast value-rows) value-rows)
                 (if footer-row? (butlast row-styles) row-styles)))
      (when footer-row?
        (into
         [tfoot {}]
         [(into [tfoot-tr {}]
                (let [footer-row (last value-rows)
                      footer-props (last row-styles)]
                  (->> footer-row
                       (map-indexed (fn [cell-idx value]
                                      (vector td
                                              (merge footer-props
                                                     (nth (:cell-props footer-props) cell-idx nil))
                                              value))))))]))]]))

(defn editable-table
  [_config props _data]
  (let [local-state (r/atom {})]
    (fn
      [{:keys [previous-data-set-reference-date
               imported-data-set-label
               previous-data-set-label
               manual-data-set-label
               manual-data-set-path
               selected-data-set
               selected-data-set-path
               editable-data->raw-data
               raw-data->editable-data
               fixed-columns?
               fixed-labels?
               fixed-rows?
               disable-heading-edit?]
        :as   config}
       {:keys [save-text save-table-data table-edit? table-select?]}
       table-view]
      (let [figures (or (:figures config)
                        config)
            template-type (:fund-template-type props)
            v2-template? (or (= template-type :lp-report-template.capman/real-estate-fund-resi-v2)
                             (= template-type :lp-report-template.capman/real-estate-fund-hotels-v2)
                             (= template-type :lp-report-template.capman/real-estate-fund-hotels-v3))
            capman-custom-toolbar? (or (and (tenant-customizations/capman?)
                                            (not v2-template?))
                                       (= (theme/get-theme) :theme.lp-portal/reindeer-partners))
            no-datasets? (every? nil? [imported-data-set-label
                                       previous-data-set-label
                                       manual-data-set-label])]
        [:div
         {:class (<class chart-style/chart-container)}
         (when (:show-data-modal? @local-state)
           [edit-table-modal/view
            {:hide!            #(swap! local-state dissoc :show-data-modal?)
             :visible?         true
             :data             (raw-data->editable-data figures)
             :local-state      local-state
             :save!            (fn [data]
                                 (let [raw-data (editable-data->raw-data data figures)]
                                   (save-table-data manual-data-set-path
                                                    (pr-str raw-data)
                                                    #(swap! local-state dissoc :show-data-modal?))))
             :fixed-columns?   fixed-columns?
             :fixed-labels?    fixed-labels?
             :fixed-rows?      fixed-rows?
             :disable-heading-edit? disable-heading-edit?
             :first-col-fixed? false}])
         [:div
          {:class (<class style/table-toolbar capman-custom-toolbar?)}
          (when (and table-edit?
                     (not no-datasets?))
            [dropdown-menu/view
             {:trigger-as-child
              [:div
               {:class "mr-2"}
               [:i.fal.fa-edit]]
              :items [{:label      "Edit table data"
                       :on-click   #(swap! local-state assoc :show-data-modal? true)
                       :icon-class "fal fa-server"}]}])
          (when table-select?
            [dropdown-menu/view
             {:trigger-as-child
              [:div
               {:class (<class chart-style/dropdown-trigger)}
               [:span
                (chart/source-label
                 {:selected-data-set selected-data-set
                  :previous-data-set-reference-date previous-data-set-reference-date
                  :no-datasets? no-datasets?})]
               [:i.fal.fa-chevron-down]]
              :items (chart/dataset-dropdown-items previous-data-set-label
                                                   imported-data-set-label
                                                   manual-data-set-label
                                                   selected-data-set-path
                                                   selected-data-set
                                                   save-text)}])]
         (when figures
           table-view)]))))                                 ;;pass table view from outside?
